import { useRef, useState } from "react";
import { removeProjectDirectory, type ProjectDirectory } from "@/api/spaces";
import {
  Button,
  ConfirmDialog,
  PocketRow,
  PocketSection,
} from "@/components/ui";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { settingsProjectsMessages } from "@/i18n/messages/settings-projects";
import { useSettingsNav } from "./settings-navigation";
import { SettingsFieldError } from "./settings-text-field";
import {
  projectError,
  ProjectPageUnavailable,
  useProjectPage,
} from "./project-page-state";
import { useFolderAccess } from "./project-folder-access";
import { copyToClipboard } from "@/lib/copy-to-clipboard";

export function FolderPage({
  projectId,
  folderId,
}: {
  projectId: string;
  folderId: string;
}) {
  const state = useProjectPage(projectId, folderId);
  if (state.loading || !state.project || !state.folder)
    return (
      <ProjectPageUnavailable loading={state.loading} retry={state.retry} />
    );
  return (
    <FolderDetail
      key={`${projectId}:${folderId}`}
      projectId={projectId}
      folder={state.folder}
    />
  );
}
function FolderDetail({
  projectId,
  folder,
}: {
  projectId: string;
  folder: ProjectDirectory;
}) {
  const t = useT(settingsProjectsMessages);
  const tc = useT(commonMessages);
  const nav = useSettingsNav();
  const permission = useFolderAccess(folder.path);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [removing, setRemoving] = useState(false);
  const busy = useRef(false);
  const access =
    permission.access?.status === "ok"
      ? t("accessAllowed")
      : permission.access?.status === "blocked"
        ? t("accessNotAllowed")
        : t("accessNotChecked");
  async function copy() {
    setError(null);
    const done = await copyToClipboard(folder.path);
    setCopied(done);
    if (!done) setError(t("copyPathError"));
  }
  async function remove() {
    if (busy.current) return;
    busy.current = true;
    setRemoving(true);
    setError(null);
    try {
      await removeProjectDirectory(projectId, folder.id);
      nav.open({ page: "projects-project", args: { projectId } });
    } catch (failure) {
      setError(projectError(failure, t("removeFolderError")));
      setConfirm(false);
    } finally {
      busy.current = false;
      setRemoving(false);
    }
  }
  return (
    <div data-testid="settings-page-folder">
      <PocketSection testId="folder-details">
        <div className="px-2 py-2">
          <p
            className="break-all font-mono text-appkit-caption text-primary"
            data-testid="folder-path"
          >
            {folder.path}
          </p>
          <Button
            size="sm"
            variant="ghost"
            data-testid="folder-copy"
            onClick={() => void copy()}
          >
            {copied ? tc("copied") : t("copyPath")}
          </Button>
        </div>
        <PocketRow
          testId="folder-access"
          title={t("folderAccess")}
          secondLine={permission.checking ? t("checking") : access}
          trailing={
            <div className="flex gap-1">
              {permission.canGrant && (
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid="folder-grant"
                  onClick={() => void permission.grant()}
                >
                  {t("grantAccess")}
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                disabled={permission.checking}
                data-testid="folder-check"
                onClick={() => void permission.refresh()}
              >
                {t("checkAgain")}
              </Button>
            </div>
          }
        />
        <PocketRow
          testId="folder-git"
          // i18n-ignore: product name
          title="Git"
          secondLine={
            folder.is_repo
              ? folder.default_base_branch || t("repository")
              : t("notConfigured")
          }
          chevron
          onSelect={() =>
            nav.push({
              page: "projects-git",
              args: { projectId, folderId: folder.id },
            })
          }
        />
        <PocketRow
          testId="folder-dev-server"
          title={t("devServer")}
          secondLine={folder.dev_server_command || t("notConfigured")}
          chevron
          onSelect={() =>
            nav.push({
              page: "projects-dev-server",
              args: { projectId, folderId: folder.id },
            })
          }
        />
        <SettingsFieldError
          error={permission.error || error}
          testId="folder-error"
        />
      </PocketSection>
      <Button
        variant="ghost"
        className="text-danger"
        disabled={removing}
        data-testid="folder-remove"
        onClick={() => setConfirm(true)}
      >
        {t("removeFolder")}
      </Button>
      {confirm && (
        <ConfirmDialog
          busy={removing}
          title={t("removeFolderTitle")}
          description={t("removeFolderDescription")}
          confirmLabel={tc("remove")}
          data-testid="folder-remove-confirm"
          onCancel={() => {
            if (!removing) setConfirm(false);
          }}
          onConfirm={() => void remove()}
        />
      )}
    </div>
  );
}
